/**
 * The consumer login form — presentation over `@4f/ui` primitives + the injected
 * {@link SessionStore} (DIP), following the console's `LoginSurface` shape. It
 * owns the two field signals and calls `session.login`; the store owns the token
 * + the api round-trip + the `4f:token` write that unlocks the surface. A domain
 * error (bad credentials, revoked) renders inline from `session.error()`.
 */
import { Button, Captcha, Form, TextInput, useT, useTheme } from '@4f/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import type { SessionStore } from '../../stores/session.ts';
import { isCaptchaSatisfied, isLoginSubmittable } from './auth-model.ts';
import { captchaSitekey } from './captcha-sitekey.ts';

export interface LoginFormProps {
  readonly session: SessionStore;
}

export function LoginForm(props: LoginFormProps): JSX.Element {
  const { t } = useT();
  const { theme } = useTheme();
  const [email, setEmail] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [captchaToken, setCaptchaToken] = createSignal<string | null>(null);

  // Read ONCE: the site key is baked at build time, so re-reading it reactively
  // would only give the widget a chance to remount mid-solve.
  const sitekey = captchaSitekey();
  // No key configured ⇒ the edge is running `NullCaptchaVerifier` and expects no
  // token, so the gate must not block. A key configured ⇒ nothing is sent until
  // the challenge is solved, because the edge refuses an empty token outright.
  const captchaOk = (): boolean => isCaptchaSatisfied(sitekey, captchaToken());

  const [resetKey, setResetKey] = createSignal(0);
  /**
   * A refused attempt has ALREADY spent the captcha token: the edge runs
   * `ensureHuman` before it looks at the credentials, and an hCaptcha token is
   * single-use. Resubmitting it fails the bot gate rather than the password, so
   * every retry after the first mistake would answer 403 with no way out but a
   * reload. Bumping the key re-arms the widget and clears the held token.
   */
  const reArmCaptchaIfRejected = (accepted: boolean): void => {
    if (!accepted) {
      setResetKey((n) => n + 1);
    }
  };

  /**
   * The store resolves `false` for every refusal the edge reports, so the
   * `.then` in `submit` covers the normal path. A THROW is different — unexpected, and
   * today only reachable through a storage write — but it would leave the widget
   * holding a spent token with no way back except a reload. Re-arm, then rethrow:
   * swallowing it here would also hide a genuine fault from GlitchTip, which is
   * the only reason it is not simply caught.
   */
  const reArmCaptchaAndRethrow = (cause: unknown): never => {
    reArmCaptchaIfRejected(false);
    throw cause;
  };

  const submittable = (): boolean => isLoginSubmittable(email(), password()) && captchaOk();
  const submit = (): void => {
    const token = captchaToken();
    if (submittable()) {
      void props.session
        .login({
          email: email().trim(),
          password: password(),
          ...(token ? { captchaToken: token } : {}),
        })
        .then(reArmCaptchaIfRejected)
        .catch(reArmCaptchaAndRethrow);
    }
  };

  return (
    <Form class="f4-auth__form" onSubmit={submit}>
      <TextInput
        id="login-email"
        label={t('auth.email')}
        type="email"
        value={email()}
        onValue={setEmail}
        autocomplete="username"
        required
      />
      <TextInput
        id="login-password"
        label={t('auth.password')}
        type="password"
        value={password()}
        onValue={setPassword}
        autocomplete="current-password"
        required
      />

      <Show when={sitekey}>
        {(key) => (
          <Captcha
            sitekey={key()}
            resetKey={resetKey()}
            theme={theme()}
            errorLabel={t('auth.captcha.unavailable')}
            onToken={setCaptchaToken}
          />
        )}
      </Show>

      <Show when={props.session.error()}>
        {(error) => (
          <p class="f4-auth__error" role="alert">
            {error().message}
          </p>
        )}
      </Show>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        loading={props.session.loading()}
        loadingLabel={t('auth.loading')}
        disabled={!submittable()}
      >
        {t('auth.login.submit')}
      </Button>
    </Form>
  );
}
